"use client";

import { c as _c } from "react/compiler-runtime";
import { Loader2, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { ChatSearchDialog } from "./chat-search-dialog";
export function useIsMac() {
  const $ = _c(2);
  const [isMac, setIsMac] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsMac(/(Mac|iPhone|iPad|iPod)/i.test(window.navigator.platform));
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isMac;
}

// Global shortcut on the platform's sidebar modifier (⌘ on Mac, Alt elsewhere,
// matching the existing search shortcut convention).
export function useSidebarShortcut(key, handler, t0) {
  const $ = _c(6);
  const enabled = t0 === undefined ? true : t0;
  const isMac = useIsMac();
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== handler || $[2] !== isMac || $[3] !== key) {
    t1 = () => {
      if (!enabled || !handler) {
        return;
      }
      const handleKeyDown = event => {
        const target = event.target;
        if (target instanceof HTMLElement && target.closest("input, textarea, select, [role=\"textbox\"], [contenteditable]:not([contenteditable=\"false\"])")) {
          return;
        }
        const pressed = event.key?.toLowerCase();
        const withModifier = isMac ? event.metaKey && !event.altKey && !event.ctrlKey : event.altKey && !event.metaKey && !event.ctrlKey;
        if (pressed !== key || !withModifier || event.shiftKey || event.defaultPrevented) {
          return;
        }
        event.preventDefault();
        handler();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t2 = [key, handler, enabled, isMac];
    $[0] = enabled;
    $[1] = handler;
    $[2] = isMac;
    $[3] = key;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return isMac;
}
export function SidebarShortcutKbd(t0) {
  const $ = _c(2);
  const {
    keys
  } = t0;
  let t1;
  if ($[0] !== keys) {
    t1 = <kbd className="ml-auto text-xs font-medium text-muted-foreground opacity-0 transition-opacity group-hover/menu-item:opacity-100 group-focus-within/menu-item:opacity-100 group-data-[collapsible=icon]:hidden">{keys}</kbd>;
    $[0] = keys;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
interface SidebarChatSearchProps {
  // Search is only enabled on the Chat page, where selecting a result can
  // switch chats in place; elsewhere the entry point renders disabled.
  disabled?: boolean;
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
}
export function SidebarChatSearch(t0) {
  const $ = _c(18);
  const {
    disabled: t1,
    onChatSelect,
    onNewChat
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsSearchOpen(true);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const isMac = useSidebarShortcut("k", t2, !disabled);
  let t3;
  let t4;
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => setIsSearchOpen(true);
    t4 = <Search className="h-4 w-4" />;
    t5 = <span>Search Chats</span>;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== disabled || $[5] !== isMac) {
    t6 = !disabled && <SidebarShortcutKbd keys={isMac ? "\u2318K" : "Alt+K"} />;
    $[4] = disabled;
    $[5] = isMac;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== disabled || $[8] !== t6) {
    t7 = <SidebarMenuItem><SidebarMenuButton type="button" tooltip="Search Chats" disabled={disabled} onClick={t3}>{t4}{t5}{t6}</SidebarMenuButton></SidebarMenuItem>;
    $[7] = disabled;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== disabled || $[11] !== isSearchOpen || $[12] !== onChatSelect || $[13] !== onNewChat) {
    t8 = !disabled && <ChatSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} onNewChat={onNewChat} onChatSelect={onChatSelect} />;
    $[10] = disabled;
    $[11] = isSearchOpen;
    $[12] = onChatSelect;
    $[13] = onNewChat;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}
interface SidebarNewActionProps {
  label: string;
  onAction?: () => void;
  isLoading?: boolean;
}
export function SidebarNewAction(t0) {
  const $ = _c(13);
  const {
    label,
    onAction,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const isMac = useSidebarShortcut("j", onAction, !isLoading);
  let t2;
  if ($[0] !== isLoading) {
    t2 = isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />;
    $[0] = isLoading;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== label) {
    t3 = <span>{label}</span>;
    $[2] = label;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isMac ? "\u2318J" : "Alt+J";
  let t5;
  if ($[4] !== t4) {
    t5 = <SidebarShortcutKbd keys={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== isLoading || $[7] !== label || $[8] !== onAction || $[9] !== t2 || $[10] !== t3 || $[11] !== t5) {
    t6 = <SidebarMenuItem><SidebarMenuButton onClick={onAction} disabled={isLoading} tooltip={label} className="border border-border">{t2}{t3}{t5}</SidebarMenuButton></SidebarMenuItem>;
    $[6] = isLoading;
    $[7] = label;
    $[8] = onAction;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}